import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../services/clinicApi";
import { useTrainingMode } from "../training";
import { useIsMobile } from "../hooks/useMediaQuery";
import {
  ACTIVATION_STEPS,
  evaluateActivationSteps,
  getActivationStep,
  getCoachSubsteps,
  isMoreMenuNavSubstep,
  isQuickSetupComplete,
  loadActivationMeta,
  matchesActivationRoute,
  navIdFromTourTarget,
  normalizeTourSubstepId,
  QUICK_START_ACCEPTED_EVENT,
  saveActivationMeta,
  setupToActivationFlags,
  TRAINING_QUICK_LESSON_IDS,
} from "./activationPath";
import { ensureQuickStartDefaults } from "./ensureQuickStartDefaults";
import {
  dismissOnboarding,
  resetOnboarding,
} from "../components/Clinic/AdminOnboarding/onboardingStorage";
import { isCoreBottomTab } from "../utils/adminNav";
import {
  isQuickStartFeatureOn,
  isQuickStartAutoStart,
} from "../utils/productFlags";

const ActivationContext = createContext(null);

function ActivationProviderEnabled({ children }) {
  const tenant = useSelector((state) => state.auth?.tenant);
  const tenantId = tenant?.id;
  const autoStart = isQuickStartAutoStart(tenant);
  const training = useTrainingMode();
  const trainingActive = Boolean(training?.active);
  const location = useLocation();
  const isMobile = useIsMobile(768);
  const navOpts = useMemo(
    () => ({ isMobile, skipNav: false }),
    [isMobile],
  );

  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(() => loadActivationMeta(tenantId));
  const [collapsed, setCollapsed] = useState(
    () => loadActivationMeta(tenantId).collapsed ?? true,
  );
  const [substepByStep, setSubstepByStep] = useState(
    () => loadActivationMeta(tenantId).substepByStep || {},
  );
  const substepByStepRef = useRef(substepByStep);
  substepByStepRef.current = substepByStep;

  const setSubstep = useCallback(
    (stepId, subId) => {
      setSubstepByStep((prev) => {
        const next = { ...prev, [stepId]: subId };
        if (tenantId) {
          saveActivationMeta(tenantId, { substepByStep: next });
        }
        return next;
      });
    },
    [tenantId],
  );

  useEffect(() => {
    const next = loadActivationMeta(tenantId);
    setMeta(next);
    setCollapsed(Boolean(next.collapsed));
    setSubstepByStep(next.substepByStep || {});
  }, [tenantId]);

  const refresh = useCallback(async () => {
    if (!tenantId || trainingActive) {
      setLoading(false);
      return null;
    }
    try {
      const status = await clinicAPI.getClinicSetupStatus();
      const withLocal = {
        ...status,
        parent_invite_seen: Boolean(
          loadActivationMeta(tenantId).parentInviteSeen,
        ),
      };
      setSetup(withLocal);
      return withLocal;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [tenantId, trainingActive]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (trainingActive || !tenantId) return undefined;
    const onFocus = () => refresh();
    const id = window.setInterval(() => refresh(), 12000);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh, trainingActive, tenantId]);

  const evaluation = useMemo(() => {
    const base = evaluateActivationSteps(setup || {}, meta.skipped || []);
    const flags = {
      ...base.flags,
      hasParentInvite: Boolean(meta.parentInviteSeen),
    };
    const completed = ACTIVATION_STEPS.filter((s) => flags[s.predicate]).map(
      (s) => s.id,
    );
    const skipped = new Set(meta.skipped || []);
    const doneSet = new Set([...completed, ...skipped]);
    const current =
      ACTIVATION_STEPS.find((s) => !doneSet.has(s.id)) || null;
    const allDone = ACTIVATION_STEPS.every(
      (s) => completed.includes(s.id) || skipped.has(s.id),
    );
    return {
      flags,
      completed,
      skipped: [...skipped],
      current,
      allDone,
      progressCount: completed.length,
      totalSteps: ACTIVATION_STEPS.length,
    };
  }, [setup, meta]);

  const quickSetupDone = isQuickSetupComplete(setup || {}, meta.skipped);
  const tourLessonsComplete = useMemo(() => {
    const tourDone = new Set(meta.mobileTourDoneIds || []);
    const skipped = new Set(meta.skipped || []);
    if (meta.walkthroughReplay) {
      return TRAINING_QUICK_LESSON_IDS.every(
        (id) => tourDone.has(id) || skipped.has(id),
      );
    }
    return TRAINING_QUICK_LESSON_IDS.every((id) => {
      const step = getActivationStep(id);
      if (!step) return true;
      if (skipped.has(id) || tourDone.has(id)) return true;
      // Реальный прогресс в клубе, а не только клики по подсказкам тура
      if (evaluation.flags[step.predicate]) return true;
      return false;
    });
  }, [
    evaluation.flags,
    meta.mobileTourDoneIds,
    meta.skipped,
    meta.walkthroughReplay,
  ]);
  const onboardingSteps = useMemo(
    () =>
      TRAINING_QUICK_LESSON_IDS.map((id) => getActivationStep(id)).filter(
        Boolean,
      ),
    [],
  );
  const onboardingCurrent = useMemo(() => {
    const skipped = new Set(meta.skipped || []);
    const tourDone = new Set(meta.mobileTourDoneIds || []);
    for (const step of onboardingSteps) {
      if (meta.mobileTourFromStart || meta.walkthroughReplay) {
        if (tourDone.has(step.id) || skipped.has(step.id)) continue;
        // Повторный проход — все подсказки по очереди
        if (meta.walkthroughReplay) return step;
        // Уже есть реальный результат в клубе — не застреваем на подсказке
        if (evaluation.flags[step.predicate]) continue;
        return step;
      }
      if (evaluation.flags[step.predicate] || skipped.has(step.id)) continue;
      return step;
    }
    return null;
  }, [
    evaluation.flags,
    meta.skipped,
    meta.mobileTourFromStart,
    meta.walkthroughReplay,
    meta.mobileTourDoneIds,
    onboardingSteps,
  ]);
  const onboardingProgressCount = useMemo(() => {
    if (meta.mobileTourFromStart || meta.walkthroughReplay) {
      return (meta.mobileTourDoneIds || []).length;
    }
    return onboardingSteps.filter((step) =>
      evaluation.completed.includes(step.id),
    ).length;
  }, [
    evaluation.completed,
    meta.mobileTourFromStart,
    meta.walkthroughReplay,
    meta.mobileTourDoneIds,
    onboardingSteps,
  ]);

  const tourCompletedIds = useMemo(() => {
    if (meta.mobileTourFromStart || meta.walkthroughReplay) {
      return meta.mobileTourDoneIds || [];
    }
    return evaluation.completed;
  }, [
    meta.mobileTourFromStart,
    meta.walkthroughReplay,
    meta.mobileTourDoneIds,
    evaluation.completed,
  ]);

  const patchMeta = useCallback(
    (patch) => {
      if (!tenantId) return;
      saveActivationMeta(tenantId, patch);
      setMeta(loadActivationMeta(tenantId));
    },
    [tenantId],
  );

  const currentSubsteps = useMemo(() => {
    let list = getCoachSubsteps(onboardingCurrent, navOpts);
    if (!onboardingCurrent) return list;

    const onLessonRoute =
      onboardingCurrent.route &&
      matchesActivationRoute(
        location.pathname,
        location.search,
        onboardingCurrent.route,
      );

    // На телефоне, если уже в нужном разделе — без шагов «Ещё» / пункт меню
    if (isMobile && onLessonRoute) {
      const content = list.filter((s) => s.kind !== "nav");
      if (content.length) list = content;
    }

    // Расписание: уже внутри группы — без «откройте группу»
    if (onboardingCurrent.id === "schedule") {
      const cur =
        substepByStep[onboardingCurrent.id] ||
        (onLessonRoute ? "sched_fill" : null);
      if (cur === "sched_fill" || cur === "sched_save" || onLessonRoute) {
        list = list.filter((s) => s.id !== "sched_pick_group" && s.kind !== "nav");
      }
    }

    return list;
  }, [
    onboardingCurrent,
    navOpts,
    isMobile,
    location.pathname,
    location.search,
    substepByStep,
  ]);

  const rawSubstepId = onboardingCurrent
    ? substepByStep[onboardingCurrent.id] || currentSubsteps[0]?.id || null
    : null;
  const currentSubstepId = normalizeTourSubstepId(
    onboardingCurrent?.id,
    rawSubstepId,
  );

  const currentSubstep = useMemo(
    () =>
      currentSubsteps.find((s) => s.id === currentSubstepId) ||
      currentSubsteps[0] ||
      null,
    [currentSubsteps, currentSubstepId],
  );

  // Если текущий подшаг отфильтрован (nav/pick) — перейти на актуальный
  useEffect(() => {
    if (!onboardingCurrent?.id || !currentSubsteps.length) return;
    if (currentSubsteps.some((s) => s.id === currentSubstepId)) return;
    const next = currentSubsteps[0];
    if (next) setSubstep(onboardingCurrent.id, next.id);
  }, [
    onboardingCurrent?.id,
    currentSubsteps,
    currentSubstepId,
    setSubstep,
  ]);

  useEffect(() => {
    if (!onboardingCurrent?.id || !rawSubstepId) return;
    const normalized = normalizeTourSubstepId(onboardingCurrent.id, rawSubstepId);
    if (normalized !== rawSubstepId) {
      setSubstep(onboardingCurrent.id, normalized);
    }
  }, [onboardingCurrent?.id, rawSubstepId, setSubstep]);

  const completeMobileTourStep = useCallback(
    (stepId, setupOverride = null) => {
      if ((!meta.mobileTourFromStart && !meta.walkthroughReplay) || !stepId) {
        return;
      }
      const step = getActivationStep(stepId);
      const flags = setupOverride
        ? {
            ...setupToActivationFlags(setupOverride),
            hasParentInvite: Boolean(meta.parentInviteSeen),
          }
        : evaluation.flags;
      // Повторный проход: можно закрыть урок без повторного действия в клубе
      if (
        !meta.walkthroughReplay &&
        step?.predicate &&
        !step.optional &&
        !flags[step.predicate]
      ) {
        return;
      }
      const done = Array.from(
        new Set([...(meta.mobileTourDoneIds || []), stepId]),
      );
      patchMeta({ mobileTourDoneIds: done });
      setSubstepByStep((prev) => {
        const next = { ...prev };
        delete next[stepId];
        if (tenantId) {
          saveActivationMeta(tenantId, { substepByStep: next });
        }
        return next;
      });
    },
    [
      evaluation.flags,
      meta.mobileTourFromStart,
      meta.walkthroughReplay,
      meta.mobileTourDoneIds,
      meta.parentInviteSeen,
      patchMeta,
      tenantId,
    ],
  );

  const advanceSubstep = useCallback(() => {
    const step = onboardingCurrent;
    if (!step) return null;
    let list = getCoachSubsteps(step, navOpts);
    const onLessonRoute =
      step.route &&
      matchesActivationRoute(
        location.pathname,
        location.search,
        step.route,
      );
    if (isMobile && onLessonRoute) {
      const content = list.filter((s) => s.kind !== "nav");
      if (content.length) list = content;
    }
    if (step.id === "schedule" && onLessonRoute) {
      list = list.filter((s) => s.id !== "sched_pick_group" && s.kind !== "nav");
    }
    if (!list.length) return null;
    const curId = normalizeTourSubstepId(
      step.id,
      substepByStepRef.current[step.id] || list[0].id,
    );
    const idx = list.findIndex((s) => s.id === curId);
    const next = list[idx + 1];
    if (next) {
      setSubstep(step.id, next.id);
      return next;
    }
    completeMobileTourStep(step.id);
    return null;
  }, [
    completeMobileTourStep,
    onboardingCurrent,
    navOpts,
    setSubstep,
    isMobile,
    location.pathname,
    location.search,
  ]);

  /** Complete the current lesson regardless of which substep we're on. */
  const finishCurrentStep = useCallback(
    (setupOverride = null) => {
      const step = onboardingCurrent;
      if (!step) return;
      completeMobileTourStep(step.id, setupOverride);
    },
    [completeMobileTourStep, onboardingCurrent],
  );

  const skipToContentSubstep = useCallback(
    (step) => {
      if (!step) return;
      const list = getCoachSubsteps(step, navOpts);
      const firstContent = list.find((item) => item.kind !== "nav");
      if (firstContent) setSubstep(step.id, firstContent.id);
    },
    [navOpts, setSubstep],
  );

  const skipStep = useCallback(
    (id, { force = false } = {}) => {
      const step = getActivationStep(id);
      // Обязательные шаги быстрого старта нельзя пропустить кликом
      if (
        !force &&
        step &&
        !step.optional &&
        TRAINING_QUICK_LESSON_IDS.includes(id) &&
        !evaluation.flags[step.predicate]
      ) {
        return;
      }
      const nextSkipped = Array.from(new Set([...(meta.skipped || []), id]));
      const nextDone = Array.from(
        new Set([...(meta.mobileTourDoneIds || []), id]),
      );
      patchMeta({ skipped: nextSkipped, mobileTourDoneIds: nextDone });
      setSubstepByStep((prev) => {
        if (!prev?.[id]) return prev;
        const next = { ...prev };
        delete next[id];
        if (tenantId) {
          saveActivationMeta(tenantId, { substepByStep: next });
        }
        return next;
      });
    },
    [
      evaluation.flags,
      meta.skipped,
      meta.mobileTourDoneIds,
      patchMeta,
      tenantId,
    ],
  );

  const markParentInviteSeen = useCallback(() => {
    patchMeta({ parentInviteSeen: true });
    setSetup((prev) =>
      prev ? { ...prev, parent_invite_seen: true } : prev,
    );
  }, [patchMeta]);

  const dismiss = useCallback(() => {
    patchMeta({ dismissed: true });
    if (tenantId) dismissOnboarding(tenantId);
  }, [patchMeta, tenantId]);

  const reopen = useCallback(() => {
    if (tenantId) resetOnboarding(tenantId);
    patchMeta({
      dismissed: false,
      skipped: [],
      substepByStep: {},
      collapsed: true,
      onboardingOpened: true,
      welcomeSeen: true,
      celebrationSeen: false,
      tourPaused: false,
      mobileTourFromStart: true,
      walkthroughReplay: true,
      mobileTourDoneIds: [],
      tourVisits: {},
    });
    setSubstepByStep({});
    setCollapsed(true);
    void ensureQuickStartDefaults()
      .then(() => refresh())
      .catch(() => refresh());
  }, [patchMeta, refresh, tenantId]);

  const restartMobileQuickStart = useCallback(() => {
    if (!isMobile) return;
    if (tenantId) resetOnboarding(tenantId);
    patchMeta({
      dismissed: false,
      skipped: [],
      substepByStep: {},
      collapsed: true,
      onboardingOpened: true,
      welcomeSeen: true,
      celebrationSeen: false,
      tourPaused: false,
      mobileTourFromStart: true,
      walkthroughReplay: true,
      mobileTourDoneIds: [],
      tourVisits: {},
    });
    setSubstepByStep({});
    setCollapsed(true);
    void ensureQuickStartDefaults()
      .then(() => refresh())
      .catch(() => refresh());
  }, [isMobile, patchMeta, refresh, tenantId]);

  const acceptWelcome = useCallback(
    (opts = {}) => {
      patchMeta({
        welcomeSeen: true,
        onboardingOpened: true,
        collapsed: true,
        dismissed: false,
        celebrationSeen: false,
        tourPaused: false,
        substepByStep: {},
        skipped: [],
        mobileTourFromStart: true,
        walkthroughReplay: false,
        mobileTourDoneIds: [],
        tourVisits: {},
      });
      setSubstepByStep({});
      setCollapsed(true);
      void ensureQuickStartDefaults({
        directionName: opts.directionName,
      })
        .then(() => refresh())
        .catch(() => refresh());
      if (opts.startTraining && !isMobile) {
        window.dispatchEvent(
          new CustomEvent(QUICK_START_ACCEPTED_EVENT, {
            detail: opts.trainingOpts || {},
          }),
        );
      }
    },
    [isMobile, patchMeta, refresh],
  );

  const patchCelebrationSeen = useCallback(() => {
    patchMeta({ celebrationSeen: true });
  }, [patchMeta]);

  const skipWelcome = useCallback(
    (opts = {}) => {
      patchMeta({ welcomeSeen: true });
      if (opts.fromTraining) return;
      dismiss();
    },
    [dismiss, patchMeta],
  );

  const setCoachCollapsed = useCallback(
    (value) => {
      if (isMobile) {
        setCollapsed(true);
        patchMeta({ collapsed: true });
        return;
      }
      const next = typeof value === "function" ? value(collapsed) : value;
      setCollapsed(next);
      patchMeta({ collapsed: Boolean(next) });
    },
    [collapsed, isMobile, patchMeta],
  );


  const structureReady =
    evaluation.flags.hasDirection &&
    evaluation.flags.hasVenue &&
    evaluation.flags.hasGroup;
  const manualReplay = Boolean(
    meta.mobileTourFromStart || meta.walkthroughReplay,
  );
  // Новые клубы — авто-тур; старые — только после «Быстрый старт» / перезапуска
  const visible =
    Boolean(tenantId) &&
    !trainingActive &&
    !meta.dismissed &&
    !loading &&
    (autoStart
      ? !quickSetupDone || manualReplay
      : manualReplay);

  const welcomeOpen =
    visible &&
    autoStart &&
    !meta.welcomeSeen &&
    !meta.onboardingOpened;

  const tourPaused = Boolean(meta.tourPaused);
  const tourActive =
    visible && Boolean(meta.onboardingOpened) && !tourPaused;

  const pauseTour = useCallback(() => {
    patchMeta({ tourPaused: true });
  }, [patchMeta]);

  const resumeTour = useCallback(() => {
    patchMeta({ tourPaused: false });
  }, [patchMeta]);

  const finishTourSession = useCallback(() => {
    patchMeta({
      dismissed: true,
      mobileTourFromStart: false,
      walkthroughReplay: false,
      tourPaused: false,
      celebrationSeen: true,
    });
    if (tenantId) dismissOnboarding(tenantId);
  }, [patchMeta, tenantId]);

  // Reset substep when moving to a new incomplete step
  useEffect(() => {
    const step = onboardingCurrent;
    if (!step) return;
    const list = getCoachSubsteps(step, navOpts);
    if (!list.length) return;
    const cur = substepByStep[step.id];
    const curSub = list.find((s) => s.id === cur) || null;
    const onLessonRoute =
      step.route &&
      matchesActivationRoute(
        location.pathname,
        location.search,
        step.route,
      );

    if (
      curSub &&
      curSub.kind !== "nav" &&
      list[0]?.kind === "nav" &&
      step.route &&
      !onLessonRoute
    ) {
      setSubstep(step.id, list[0].id);
      return;
    }

    if (!cur || !list.some((s) => s.id === cur)) {
      // По умолчанию всегда с «Ещё» / пункта меню — учим путь.
      // Исключение: schedule после создания группы (уже внутри карточки).
      let start = list[0];
      if (
        step.id === "schedule" &&
        onLessonRoute &&
        list[0]?.kind === "nav"
      ) {
        const content = list.filter((s) => s.kind !== "nav");
        start =
          content.find((s) => s.id === "sched_fill") ||
          content[0] ||
          list[0];
      }
      setSubstep(step.id, start.id);
    }
  }, [
    location.pathname,
    location.search,
    onboardingCurrent,
    navOpts,
    setSubstep,
    substepByStep,
  ]);

  // Auto-advance / rewind nav substeps (mobile hub + lesson routes)
  useEffect(() => {
    const step = onboardingCurrent;
    if (!tourActive || !step || currentSubstep?.kind !== "nav") return;

    const path = location.pathname.replace(/\/$/, "");
    const onLessonRoute =
      step.route &&
      matchesActivationRoute(location.pathname, location.search, step.route);
    const list = getCoachSubsteps(step, navOpts);
    const firstContent =
      step.id === "schedule"
        ? list.find((s) => s.id === "sched_fill") ||
          list.find((s) => s.kind !== "nav")
        : list.find((s) => s.kind !== "nav");

    // Только schedule: уже в карточке группы → сразу контент, без «Ещё»
    const skipNavOnRoute = step.id === "schedule" && onLessonRoute;

    if (currentSubstep.id.endsWith("_nav_hub")) {
      if (skipNavOnRoute && firstContent) {
        setSubstep(step.id, firstContent.id);
      } else if (path === "/manage/app/more") {
        advanceSubstep();
      }
      return;
    }

    if (skipNavOnRoute && isMoreMenuNavSubstep(currentSubstep.id)) {
      if (firstContent) setSubstep(step.id, firstContent.id);
      else advanceSubstep();
      return;
    }

    if (
      isMobile &&
      isMoreMenuNavSubstep(currentSubstep.id) &&
      !onLessonRoute
    ) {
      const navId = navIdFromTourTarget(currentSubstep.target);
      if (navId && !isCoreBottomTab(navId) && path !== "/manage/app/more") {
        setSubstep(step.id, `${step.id}_nav_hub`);
      }
      return;
    }

    // После клика по пункту меню / вкладке — переходим к контенту шага
    if (
      currentSubstep.id.endsWith("_nav") &&
      !currentSubstep.id.endsWith("_nav_hub") &&
      onLessonRoute
    ) {
      if (firstContent) setSubstep(step.id, firstContent.id);
      else advanceSubstep();
    }
  }, [
    tourActive,
    onboardingCurrent,
    currentSubstep,
    location.pathname,
    location.search,
    isMobile,
    advanceSubstep,
    setSubstep,
    navOpts,
  ]);

  const value = {
    visible,
    welcomeOpen,
    tourActive,
    mobileTourReplay: Boolean(
      meta.mobileTourFromStart || meta.walkthroughReplay,
    ),
    walkthroughReplay: Boolean(meta.walkthroughReplay),
    quickStartTourDoneIds: meta.mobileTourDoneIds || [],
    acceptWelcome,
    skipWelcome,
    celebrationSeen: Boolean(meta.celebrationSeen),
    patchCelebrationSeen,
    loading,
    setup,
    steps: onboardingSteps,
    ...evaluation,
    completed: tourCompletedIds,
    allDone: meta.walkthroughReplay ? tourLessonsComplete : quickSetupDone,
    tourLessonsComplete,
    quickSetupDone,
    progressCount: onboardingProgressCount,
    totalSteps: onboardingSteps.length,
    currentStep: onboardingCurrent,
    currentSubsteps,
    currentSubstep,
    currentSubstepId,
    setSubstep,
    advanceSubstep,
    finishCurrentStep,
    skipToContentSubstep,
    collapsed,
    setCollapsed: setCoachCollapsed,
    refresh,
    skipStep,
    dismiss,
    reopen,
    restartMobileQuickStart,
    pauseTour,
    resumeTour,
    finishTourSession,
    tourPaused,
    markParentInviteSeen,
    structureReady,
  };

  return (
    <ActivationContext.Provider value={value}>
      {children}
    </ActivationContext.Provider>
  );
}

export function ActivationProvider({ children }) {
  if (!isQuickStartFeatureOn()) {
    return children;
  }
  return <ActivationProviderEnabled>{children}</ActivationProviderEnabled>;
}

export function useActivation() {
  const ctx = useContext(ActivationContext);
  if (!ctx) {
    return {
      visible: false,
      welcomeOpen: false,
      tourActive: false,
      mobileTourReplay: false,
      walkthroughReplay: false,
      quickStartTourDoneIds: [],
      acceptWelcome: () => {},
      skipWelcome: () => {},
      celebrationSeen: true,
      patchCelebrationSeen: () => {},
      steps: ACTIVATION_STEPS,
      currentStep: null,
      currentSubsteps: [],
      currentSubstep: null,
      currentSubstepId: null,
      setSubstep: () => {},
      advanceSubstep: () => null,
      finishCurrentStep: () => {},
      skipToContentSubstep: () => {},
      completed: [],
      skipped: [],
      allDone: true,
      tourLessonsComplete: true,
      quickSetupDone: true,
      progressCount: 0,
      totalSteps: ACTIVATION_STEPS.length,
      collapsed: true,
      setCollapsed: () => {},
      refresh: async () => null,
      skipStep: () => {},
      dismiss: () => {},
      reopen: () => {},
      restartMobileQuickStart: () => {},
      pauseTour: () => {},
      resumeTour: () => {},
      finishTourSession: () => {},
      tourPaused: false,
      markParentInviteSeen: () => {},
      structureReady: false,
    };
  }
  return ctx;
}
